import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiError, type Org } from '../../lib/api';
import { toast } from '@vatman/ui';
import { errorText } from '../../i18n';
import { AgentLog } from '../../ui/AgentActivity';

interface Facets {
  people: { id: string; name: string }[];
  boards: { id: string; title: string }[];
}

/** Organization settings → AI agents: the switch (admins) and the log of what agents did. */
export function OrgAgents({ org }: { org: Org }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const admin = org.role !== 'member';
  const key = ['org-ai-agents', org.id];
  const state = useQuery({ queryKey: key, queryFn: () => api.get<{ enabled: boolean }>(`/orgs/${org.id}/ai-agents`) });
  const [person, setPerson] = useState('');
  const [board, setBoard] = useState('');
  // Filter choices: the people and boards that appear in the log.
  const facets = useQuery({
    queryKey: ['org-agent-facets', org.id],
    queryFn: () => api.get<Facets>(`/orgs/${org.id}/agent-activity?limit=1`),
    enabled: admin,
  });
  const params = new URLSearchParams();
  if (person) params.set('user', person);
  if (board) params.set('board', board);
  const path = `/orgs/${org.id}/agent-activity${params.size ? `?${params}` : ''}`;

  const toggle = async (enabled: boolean) => {
    try {
      await api.put(`/orgs/${org.id}/ai-agents`, { enabled });
      qc.setQueryData(key, { enabled });
      toast(enabled ? t('agents.turnedOn') : t('agents.turnedOff'));
    } catch (e) {
      toast(errorText(e instanceof ApiError ? e.code : 'internal'));
    }
  };

  return (
    <div className="settings-card">
      <h3>{t('agents.orgTitle')}</h3>
      {state.data &&
        (admin ? (
          <label className="agent-switch">
            <input type="checkbox" checked={state.data.enabled} onChange={(e) => void toggle(e.target.checked)} />
            <span>
              <div style={{ fontWeight: 600 }}>{t('agents.allow')}</div>
              <div className="muted" style={{ fontSize: 'var(--fs-3)' }}>
                {t('agents.allowHint')}
              </div>
            </span>
          </label>
        ) : (
          <p className="muted" style={{ margin: '8px 0 0' }}>
            {state.data.enabled ? t('agents.statusOn') : t('agents.statusOff')}
          </p>
        ))}
      {admin && (
        <>
          <h3 style={{ marginTop: 20 }}>{t('agents.orgLog')}</h3>
          <div className="agent-filters">
            <select className="input sm" value={person} onChange={(e) => setPerson(e.target.value)}>
              <option value="">{t('agents.everyone')}</option>
              {facets.data?.people.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <select className="input sm" value={board} onChange={(e) => setBoard(e.target.value)}>
              <option value="">{t('agents.allBoards')}</option>
              {facets.data?.boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.title}
                </option>
              ))}
            </select>
          </div>
          <AgentLog path={path} showPerson />
        </>
      )}
    </div>
  );
}
